import React, { useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { i18next, integration, customHtml } from '@yola/ws-sdk';
import { reactHookForm } from '@yola/ws-ui';
import getCurrentPackageFeatureLimits from 'yola-editor/src/js/modules/features/accessors/get-current-package-feature-limits';
import helpCenter from 'yola-editor/src/js/modules/help-center';
import useMatchBreakpoint from 'src/js/modules/utils/custom-hooks/use-match-breakpoint';
import segment from 'src/js/modules/analytics/segment';
import CodeSnippetDialog from '../components/code-snippet-dialog';
import fieldNames from '../constants/code-snippet-field-names';

const { useForm } = reactHookForm;
const {
  trackers: { trackUpgradeTriggerClicked, trackEvent, trackCodeEditorFocused },
  constants: { events },
} = segment;
const { renderStatuses } = customHtml.constants;

const getCaptions = (isNewSnippet) => ({
  title: isNewSnippet ? i18next.t('Add code snippet') : i18next.t('Edit code snippet'),
  buttons: {
    submit: i18next.t('Submit'),
    cancel: i18next.t('Cancel'),
    delete: i18next.t('Delete code snippet'),
  },
  upgradeBanner: {
    description: i18next.t('Gain access to custom code editing by upgrading your subscription!'),
    upgrade: i18next.t('Upgrade now'),
  },
  fields: {
    name: i18next.t('Name'),
    code: i18next.t('HTML code'),
    rendering: i18next.t('Rendering:'),
  },
  required: i18next.t('This field is required'),
  notification: i18next.t('Smart templates supported'),
  notificationDescription: i18next.t(
    'Code editor supports Jinja2/Nunjucks-style templates. Wrap text in {translationTemplate} to translate it automatically across website languages. Text outside the wrapper stays the same.',
    {
      translationTemplate:
        '<span style="padding: 3px 2px; border-radius: 2px; background-color: rgba(31, 155, 191, 0.25);">{% t &quot;...&quot; %}</span>',
    }
  ),
  notificationLearnMore: i18next.t('Learn more'),
});

const getRenderOptions = () => [
  { value: renderStatuses.WEB_SITE_ONLY, label: i18next.t('Website only') },
  { value: renderStatuses.RUNTIME, label: i18next.t('Website & Editor') },
];

const getDefaultValues = (snippet) => ({
  [fieldNames.NAME]: snippet.name,
  [fieldNames.CODE]: snippet.code || '',
  [fieldNames.RENDERING]: snippet.runtimeRendering
    ? renderStatuses.RUNTIME
    : renderStatuses.WEB_SITE_ONLY,
});

const DESKTOP_VIEW_BREAKPOINT = '992px';

const CodeSnippetDialogContainer = ({
  triggerId,
  dialogType,
  codeGroup,
  snippet,
  onSubmit: onSubmitCallback,
  onCancel = Function.prototype,
  onDelete = Function.prototype,
}) => {
  const upsells = useSelector(integration.selectors.getUpsells);

  const codeEditorErrorMessage = useRef(null);

  const { handleSubmit, control, getValues, setError, clearErrors } = useForm({
    defaultValues: getDefaultValues(snippet),
    mode: 'all',
  });

  const isDesktopView = useMatchBreakpoint(DESKTOP_VIEW_BREAKPOINT);

  const captions = getCaptions(snippet.isNew);
  const renderOptions = getRenderOptions();
  const limits = getCurrentPackageFeatureLimits();
  const notificationLearnMoreUrl = helpCenter.accessors.getTranslateCustomCodeTutorialUrl();

  const isAvailable = limits.customCode.available;

  const getTraits = useCallback(() => {
    const rendering = getValues(fieldNames.RENDERING);

    return {
      triggerId,
      codeGroup,
      renderInEditor: rendering === renderStatuses.RUNTIME,
      premiumAvailable: isAvailable,
    };
  }, [getValues, triggerId, codeGroup, isAvailable]);

  const onSubmit = useCallback(
    (data) => {
      const event = snippet.isNew
        ? events.ADD_CODE_SNIPPET_DIALOG_SUBMITTED
        : events.EDIT_CODE_SNIPPET_DIALOG_SUBMITTED;

      trackEvent(events[event], {
        ...getTraits(),
        codeAdded: !!data[fieldNames.CODE].length,
      });

      if (!isAvailable) {
        return;
      }

      onSubmitCallback({
        name: data[fieldNames.NAME],
        code: data[fieldNames.CODE],
        runtimeRendering: data[fieldNames.RENDERING] === renderStatuses.RUNTIME,
      });
    },
    [isAvailable, onSubmitCallback, snippet, getTraits]
  );

  const handleCancel = useCallback(() => {
    const event = snippet.isNew
      ? events.ADD_CODE_SNIPPET_DIALOG_CANCELED
      : events.EDIT_CODE_SNIPPET_DIALOG_CANCELED;

    trackEvent(events[event], getTraits());

    onCancel();
  }, [onCancel, snippet, getTraits]);

  const handleDelete = useCallback(() => {
    trackEvent(events.DELETE_CODE_SNIPPET_TRIGGER_CLICKED, {
      triggerId,
    });

    onDelete();
  }, [onDelete, triggerId]);

  const onUpgrade = useCallback(() => {
    trackUpgradeTriggerClicked(triggerId);

    return upsells.customCode
      .onUpgrade({
        actionId: triggerId,
      })
      .catch(() => {
        // eslint-disable-next-line no-console
        console.log('Upgrade flow was canceled');
      });
  }, [triggerId, upsells]);

  const onCodeEditorFocus = useCallback(() => trackCodeEditorFocused(dialogType), [dialogType]);

  const onCodeEditorError = useCallback(
    (error) => {
      codeEditorErrorMessage.current = error;
      if (error) {
        setError(fieldNames.CODE, { type: 'manual', message: error });
      } else {
        clearErrors(fieldNames.CODE);
      }
    },
    [setError, clearErrors]
  );

  useEffect(() => {
    const event = snippet.isNew
      ? events.ADD_CODE_SNIPPET_DIALOG_DISPLAYED
      : events.EDIT_CODE_SNIPPET_DIALOG_DISPLAYED;

    trackEvent(events[event], getTraits());
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  return (
    <CodeSnippetDialog
      isDesktopView={isDesktopView}
      isNewSnippet={snippet.isNew}
      isAvailable={isAvailable}
      control={control}
      captions={captions}
      renderOptions={renderOptions}
      codeEditorErrorMessage={codeEditorErrorMessage}
      notificationLearnMoreUrl={notificationLearnMoreUrl}
      onSubmit={handleSubmit(onSubmit)}
      onCancel={handleCancel}
      onDelete={handleDelete}
      onUpgrade={onUpgrade}
      onCodeEditorFocus={onCodeEditorFocus}
      onCodeEditorError={onCodeEditorError}
    />
  );
};

CodeSnippetDialogContainer.propTypes = {
  triggerId: PropTypes.string.isRequired,
  dialogType: PropTypes.string.isRequired,
  codeGroup: PropTypes.string.isRequired,
  snippet: PropTypes.shape({
    isNew: PropTypes.bool,
    name: PropTypes.string,
    code: PropTypes.string,
    runtimeRendering: PropTypes.bool,
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  onDelete: PropTypes.func,
};

export default CodeSnippetDialogContainer;
